import React, { useState, useEffect } from 'react';
import { useNavigate, Link, useLocation } from 'react-router-dom';
import { Eye, EyeOff } from 'lucide-react';
import { useAuth } from '../context/AuthContext';
import './Login.css';

const Login = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { login, processGoogleSession, isAuthenticated, loading: authLoading } = useAuth();
  
  const [formData, setFormData] = useState({
    email: '',
    password: ''
  });
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const [processingGoogle, setProcessingGoogle] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const [showResendVerification, setShowResendVerification] = useState(false);
  const [resendingVerification, setResendingVerification] = useState(false);
  const [resendSuccess, setResendSuccess] = useState('');

  // Handle Google OAuth callback - SYNCHRONOUS detection
  useEffect(() => {
    // Check BOTH hash and query params for session_id
    let sessionId = null;
    
    // Try hash first (Emergent uses hash: #session_id=xyz)
    const hash = window.location.hash;
    if (hash && hash.includes('session_id=')) {
      sessionId = hash.split('session_id=')[1].split('&')[0];
    }
    
    // Fallback to query params (?session_id=xyz)
    if (!sessionId) {
      const params = new URLSearchParams(window.location.search);
      sessionId = params.get('session_id');
    }
    
    if (sessionId) {
      setProcessingGoogle(true);
      
      setTimeout(() => {
        processGoogleSession(sessionId).then((result) => {
          if (result.success) {
            window.history.replaceState({}, document.title, window.location.pathname);
            navigate('/dashboard', { replace: true });
          } else {
            setError(`Google Sign-In failed: ${result.error || 'Unknown error'}. Please try again or use email/password login.`);
            setProcessingGoogle(false);
            window.history.replaceState({}, document.title, window.location.pathname);
            window.scrollTo({ top: 0, behavior: 'smooth' });
          }
        }).catch((err) => {
          setError('Google Sign-In encountered an error. Please try again or use email/password login.');
          setProcessingGoogle(false);
          window.history.replaceState({}, document.title, window.location.pathname);
          window.scrollTo({ top: 0, behavior: 'smooth' });
        });
      }, 100);
    }
  }, [location, processGoogleSession, navigate]);

  // Redirect if already authenticated
  useEffect(() => {
    if (isAuthenticated && !authLoading && !processingGoogle) {
      const from = location.state?.from?.pathname || '/dashboard';
      navigate(from, { replace: true });
    }
  }, [isAuthenticated, authLoading, navigate, location, processingGoogle]);

  const handleChange = (e) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value
    });
    setError('');
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError('');

    const result = await login(formData);
    
    if (result.success) {
      const from = location.state?.from?.pathname || '/dashboard';
      navigate(from, { replace: true });
    } else {
      setError(result.error);
      setLoading(false);
      // Check if error is about email verification
      if (result.error && result.error.toLowerCase().includes('verify your email')) {
        setShowResendVerification(true);
      } else {
        setShowResendVerification(false);
      }
      // Scroll to top to show the error message
      window.scrollTo({ top: 0, behavior: 'smooth' });
    }
  };

  const handleGoogleLogin = () => {
    // REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH
    const redirectUrl = `${window.location.origin}/login`;
    const oauthAuthUrl = process.env.REACT_APP_OAUTH_AUTH_URL;
    window.location.href = `${oauthAuthUrl}?redirect=${encodeURIComponent(redirectUrl)}`;
  };

  const handleResendVerification = async () => {
    if (!formData.email) {
      setError('Please enter your email address');
      return;
    }

    setResendingVerification(true);
    setResendSuccess('');
    setError('');

    try {
      const API_URL = process.env.REACT_APP_BACKEND_URL || window.location.origin;
      const response = await fetch(`${API_URL}/api/auth/resend-verification`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ email: formData.email }),
      });

      const data = await response.json();

      if (response.ok) {
        setResendSuccess('Verification email sent! Please check your inbox.');
        setShowResendVerification(false);
        setTimeout(() => setResendSuccess(''), 5000);
      } else {
        setError(data.detail || 'Failed to resend verification email');
      }
    } catch (err) {
      setError('Network error. Please try again.');
    } finally {
      setResendingVerification(false);
    }
  };

  if (authLoading || processingGoogle) {
    return (
      <div className="login-container">
        <div className="login-box">
          <div className="loading-spinner">
            <div className="spinner"></div>
            <p>{processingGoogle ? 'Completing Google Sign-in...' : 'Loading...'}</p>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="login-container">
      <div className="login-box">
        <button 
          className="back-to-home-btn" 
          onClick={() => navigate('/')}
          type="button"
        >
          ← Back to Home
        </button>
        <div className="login-header">
          <h1>Welcome Back</h1>
          <p>Sign in to your House of Microgreens account</p>
        </div>

        {error && (
          <div className="error-message">
            <span>⚠️</span> {error}
          </div>
        )}

        {resendSuccess && (
          <div className="success-message">
            <span>✅</span> {resendSuccess}
          </div>
        )}

        {showResendVerification && (
          <div className="verification-notice">
            <p>Didn't receive the verification email?</p>
            <button
              type="button"
              onClick={handleResendVerification}
              disabled={resendingVerification}
              className="resend-verification-btn"
            >
              {resendingVerification ? 'Sending...' : 'Resend Verification Email'}
            </button>
          </div>
        )}

        <form onSubmit={handleSubmit} className="login-form">
          <div className="form-group">
            <label htmlFor="email">Email Address</label>
            <input
              type="email"
              id="email"
              name="email"
              value={formData.email}
              onChange={handleChange}
              placeholder="your@email.com"
              required
              disabled={loading}
            />
          </div>

          <div className="form-group">
            <div className="label-row">
              <label htmlFor="password">Password</label>
              <Link to="/forgot-password" className="forgot-password-link">
                Forgot Password?
              </Link>
            </div>
            <div className="password-input-wrapper">
              <input
                type={showPassword ? 'text' : 'password'}
                id="password"
                name="password"
                value={formData.password}
                onChange={handleChange}
                placeholder="••••••••"
                required
                disabled={loading}
              />
              <button
                type="button"
                className="password-toggle-btn"
                onClick={() => setShowPassword(!showPassword)}
                tabIndex="-1"
              >
                {showPassword ? <EyeOff size={20} /> : <Eye size={20} />}
              </button>
            </div>
          </div>

          <button
            type="submit"
            className="btn-login"
            disabled={loading}
          >
            {loading ? 'Signing in...' : 'Sign In'}
          </button>
        </form>

        <div className="divider">
          <span>OR</span>
        </div>

        <button
          type="button"
          className="btn-google"
          onClick={handleGoogleLogin}
          disabled={loading}
        >
          <svg width="20" height="20" viewBox="0 0 20 20" fill="none">
            <path d="M19.6 10.227c0-.709-.064-1.39-.182-2.045H10v3.868h5.382a4.6 4.6 0 01-1.996 3.018v2.51h3.232c1.891-1.742 2.982-4.305 2.982-7.35z" fill="#4285F4"/>
            <path d="M10 20c2.7 0 4.964-.895 6.618-2.423l-3.232-2.509c-.895.6-2.04.955-3.386.955-2.605 0-4.81-1.76-5.595-4.123H1.064v2.59A9.996 9.996 0 0010 20z" fill="#34A853"/>
            <path d="M4.405 11.9c-.2-.6-.314-1.24-.314-1.9 0-.66.114-1.3.314-1.9V5.51H1.064A9.996 9.996 0 000 10c0 1.614.386 3.14 1.064 4.49l3.34-2.59z" fill="#FBBC05"/>
            <path d="M10 3.977c1.468 0 2.786.505 3.823 1.496l2.868-2.868C14.959.99 12.695 0 10 0 6.09 0 2.71 2.24 1.064 5.51l3.34 2.59C5.19 5.736 7.395 3.977 10 3.977z" fill="#EA4335"/>
          </svg>
          Continue with Google
        </button>

        <div className="login-footer">
          <p>
            Don&apos;t have an account?{' '}
            <Link to="/signup">Sign up</Link>
          </p>
        </div>
      </div>
    </div>
  );
};

export default Login;
